// frontend/src/tabs/TabOverlayScope.jsx
//
// Keeps a tab's overlays inside that tab.
//
// TabsHost hides an inactive tab with `display: none` on its pane, which
// hides only DOM the tab owns. A MUI Modal renders through a Portal whose
// default container is `document.body` — outside every pane — so an overlay
// left open in one tab would paint over the next. This scope hands every
// Modal and Popover inside the tab a portal container in the tab's own
// subtree: the pane hides the overlay with its tab, and a merchant who tabs
// away mid-form finds the dialog — and what they typed — exactly as they
// left it.
//
// Both defaults are needed. Dialog and Drawer are styled Modals and read the
// `MuiModal` default. An anchored Popover (and Menu, a styled Popover) hands
// Modal its anchor's `document.body` as an explicit container, and falls back
// to the Modal default once the anchor is cleared — so a menu closed by
// clearing its anchor (ActionMenu) would change container mid-close. React
// remounts a portal whose container changes, the remounted transition never
// reports exited, and the closed menu's full-viewport root would stay over the
// page. The `MuiPopover` default gives a menu one container for its whole
// life. Popper-based overlays (Tooltip, Autocomplete's popup) are out of
// scope: their absolute positioning is measured against an offset parent,
// which moving them would change.
//
// The container must resolve on the FIRST commit — hence the id lookup, which
// only needs the div to be in the document, not a ref to have been attached.
// Held in state, the first commit would portal to the body and the second
// move it here, while MUI's aria bookkeeping stays where the modal first
// mounted: it marks the container's siblings aria-hidden while a modal is
// open, so the app root would stay aria-hidden. Pinned by "leaves the rest of
// the app reachable while a dialog is open" in TabOverlayScope.test.jsx.
//
// Dialogs and menus stay position:fixed and viewport-relative in the new
// place: nothing between .app-wrapper and the tab creates a containing block
// or a stacking context (components/layout/AppLayout.css).

import { useCallback, useId, useMemo } from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

export default function TabOverlayScope({ children }) {
  const outerTheme = useTheme();
  // Addressed by id rather than a ref: MUI resolves the container inside the
  // Portal's layout effect, by which time the div is in the document, and a
  // ref-reading closure handed to createTheme during render is exactly what
  // react-hooks/refs refuses. useId is unique per mounted tab.
  const hostId = useId();
  const hostContainer = useCallback(() => document.getElementById(hostId), [hostId]);

  // Memoised: a fresh theme object every render would restyle every MUI
  // component in the tab.
  const theme = useMemo(
    () => createTheme(outerTheme, {
      components: {
        MuiModal: { defaultProps: { container: hostContainer } },
        MuiPopover: { defaultProps: { container: hostContainer } },
      },
    }),
    [outerTheme, hostContainer],
  );

  return (
    <ThemeProvider theme={theme}>
      <div id={hostId} data-tab-overlay-host="" />
      {children}
    </ThemeProvider>
  );
}
